import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    Alert,
    Backdrop,
    Box,
    CircularProgress,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import {CountResult, ITER_NAMES, searchCounts} from '../../utils'
import {webEvents} from '../../../common/WebEvents'
import CountsSummaryBox from '../CountsSummaryBox'
import {useDispatch} from 'react-redux'
import { createError } from '../../../store/slices/error'


function ReviewLandingPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const [alertOpen, setAlertOpen] = useState(false)
    const [countsInReview, setCountsInReview] = useState<CountResult[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const dispatch = useDispatch()

    useEffect(() => {
        setIsLoading(true)
        searchCounts({
            iteration_statuses: 'in-review'
        })
            .then((c: CountResult[]) => {
                if (c.length)
                    setCountsInReview(c)
            })
            .catch(error => {
                webEvents.error(
                    error.message,
                    {'review_page': 'Counts in Review'},
                    error.stack
                )
                const causeMessage = error.cause
                    ? error.cause.messages[0]
                    : error.messages[0]

                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            }).finally(() => {
            setIsLoading(false)
        })
        return () => {
            setAlertOpen(false)
        }
    }, [])

    useEffect(() => {
        if (location.state?.submissionSuccess) {
            setAlertOpen(true)
        }
        return () => {
            setAlertOpen(false)
        }
    }, [location.state])

    const getDifferenceInDaysAndHours = (startDate: Date | null, endDate: Date | null) => {
        if (!startDate || !endDate) return ''
        const diffInMs = Math.abs(new Date(endDate).valueOf() - new Date(startDate).valueOf())
        const hours = Math.floor(diffInMs / (1000 * 60 * 60))
        const days = Math.floor(hours / 24)
        const remainingHours = hours % 24

        return `${days} days ${remainingHours} hours`
    }

    return (
        <>
            {alertOpen && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit" />} severity="success"
                    onClose={() => {
                        location.state.submissionSuccess = false
                        setAlertOpen(false)
                    }}>
                    Approved Report successfully!
                </Alert>
            )}
            <Box sx={{maxWidth: 1200, mx: 'auto', my: 4, p: 2, width: '100%'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="h4" component="h2" gutterBottom
                                sx={{flexGrow: 1, textAlign: 'center', mb: 1}}>
                        Iterations Review
                    </Typography>
                </Box>
                <CountsSummaryBox />
                <Backdrop open={isLoading} className="z-50">
                    <CircularProgress color="inherit" />
                </Backdrop>
                <TableContainer component={Paper} sx={{maxHeight: 700, my: 2, width: '100%'}}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{display: 'none'}}></TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Count</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Type</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Truck</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Technician</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Submitted Date</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Duration</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Iteration</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Value Gained</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Value Lost</TableCell>
                                <TableCell style={{fontWeight: 'bold'}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {countsInReview.length > 0 ? countsInReview.map((count) => {
                                const totalGained = count.iteration.reviewStats.totalGainedValue
                                    .toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})

                                const totalLost = count.iteration.reviewStats.totalLostValue
                                    .toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})

                                return (
                                    <TableRow key={count.countId} hover>
                                        <TableCell>{count.countId}</TableCell>
                                        <TableCell>{count.type.charAt(0).toUpperCase() + count.type.slice(1)}</TableCell>
                                        <TableCell>
                                            {count.locationId}
                                        </TableCell>
                                        <TableCell>
                                            {count.iteration.assignedTo}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(count.iteration.submittedAt ?? '').toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            {getDifferenceInDaysAndHours(count.iteration?.submittedAt, count.iteration.assignedAt)}
                                        </TableCell>
                                        <TableCell>
                                            {ITER_NAMES[count.iteration.iterationId]}
                                        </TableCell>
                                        <TableCell>
                                            ${totalGained}
                                        </TableCell>
                                        <TableCell>
                                            ${totalLost}
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => navigate(`/inventory/counts/review/${count.countId}`)}
                                            >
                                                Review
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                )
                            }) : !isLoading ? <TableRow>
                                <TableCell colSpan={7} align="center" sx={{padding: '20px 0'}}>
                                    <Typography variant="h6"
                                                sx={{bgcolor: 'background.default', color: 'text.secondary'}}>
                                        No data to display
                                    </Typography>
                                </TableCell>
                            </TableRow> : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

export default ReviewLandingPage
